import React, { useMemo, useState } from 'react'
import { parseJsonErrorDetail } from './jsonExampleUtils'

const LINE_HEIGHT = 22
const PADDING_Y = 12
const GUTTER_WIDTH = 56

interface JsonCodeTextareaProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'onChange'> {
  value?: string
  onChange?: (value: string) => void
}

function JsonCodeTextarea({ value = '', onChange, rows = 8, className, style, ...props }: JsonCodeTextareaProps): JSX.Element {
  const [scrollTop, setScrollTop] = useState(0)
  const text = value || ''
  const visibleRows = Number(rows) || 8
  const errorDetail = useMemo(() => parseJsonErrorDetail(text), [text])
  const lineCount = useMemo(() => Math.max(visibleRows, text.split('\n').length || 1), [visibleRows, text])
  const lineNumbers = useMemo(() => Array.from({ length: lineCount }, (_, index) => index + 1), [lineCount])
  const editorHeight = PADDING_Y * 2 + visibleRows * LINE_HEIGHT
  const highlightTop = errorDetail?.line ? PADDING_Y + (errorDetail.line - 1) * LINE_HEIGHT - scrollTop : null

  return (
    <div
      className='overflow-hidden rounded border border-gray-300 bg-white'
      style={style}
    >
      <div className='flex'>
        <div
          className='shrink-0 overflow-hidden border-r border-gray-200 bg-gray-50 text-right text-xs text-gray-400 select-none'
          style={{ width: GUTTER_WIDTH, height: editorHeight }}
        >
          <div
            style={{
              paddingTop: PADDING_Y,
              paddingBottom: PADDING_Y,
              transform: `translateY(-${scrollTop}px)`,
            }}
          >
            {lineNumbers.map((lineNumber) => (
              <div
                key={lineNumber}
                className={errorDetail?.line === lineNumber ? 'bg-red-50 font-medium text-red-500' : undefined}
                style={{
                  height: LINE_HEIGHT,
                  lineHeight: `${LINE_HEIGHT}px`,
                  paddingRight: 12,
                }}
              >
                {lineNumber}
              </div>
            ))}
          </div>
        </div>
        <div className='relative flex-1 overflow-hidden' style={{ height: editorHeight }}>
          {highlightTop !== null ? (
            <div
              className='pointer-events-none absolute left-0 right-0 bg-red-50'
              style={{
                top: highlightTop,
                height: LINE_HEIGHT,
              }}
            />
          ) : null}
          <textarea
            {...props}
            value={text}
            spellCheck={false}
            className={`relative z-10 block w-full resize-none overflow-y-auto border-0 bg-transparent px-3 py-3 font-mono text-sm text-gray-900 focus:outline-none ${className || ''}`}
            style={{
              height: editorHeight,
              boxSizing: 'border-box',
              lineHeight: `${LINE_HEIGHT}px`,
              tabSize: 2,
            }}
            onChange={(event) => onChange?.(event.target.value)}
            onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}
          />
        </div>
      </div>
      {errorDetail?.line ? (
        <div className='border-t border-red-100 bg-red-50 px-3 py-2 text-xs text-red-500'>
          第 {errorDetail.line} 行格式不正确{errorDetail.column ? `（第 ${errorDetail.column} 列附近）` : ''}
        </div>
      ) : null}
    </div>
  )
}

export default JsonCodeTextarea
